import { useState, useEffect, useRef, useCallback } from "react";
import { message, Modal } from "antd";
import Axios from "axios";
import debounce from "lodash/debounce";
import { useTranslation } from "react-i18next";
import db from "../../../../database";
import { v4 as uuidv4 } from "uuid";
import { syncSingleProductByValue } from "../../../../services";
import useAuthStore from "../../../../stores/useAuthStore";
import { barcodeScaner } from "../scanner";
import { stock } from "../stock";
import { getApplicablePrice } from "../../../../utils/getPriceList";
import { shouldUseBatchPrice } from "../../../../utils/resolvePricingPolicy";
import messages from "../../../../errors/messages.en-IN.json";
import useCartStore from "../../../../stores/useCartStore";
import useTransactionStore from "../../../../stores/useTransactionStore";
import useSafeTimeout from "../../../../hooks/useSafeTimeout";

// ─────────────────────────────────────────────────────────
// Shared helper: builds a productDefined object used in
// addDefinedProduct, pickProduct, getSearchedProducts, and
// getSearchedItem. Eliminates 4x duplication of ~75 fields.
// ─────────────────────────────────────────────────────────

/**
 * buildProductDefined
 *
 * @param {Object}  obj        – the base product object from db
 * @param {Object}  [batchItem] – optional batch entry (mBatch element)
 * @param {Object}  [options]   – overrides / extra fields
 * @param {string}  [options.upc]
 * @param {string}  [options.batchno]
 * @param {string}  [options.mBatchId]
 * @param {number}  [options.price]       – sale price override (used by addDefinedProduct)
 * @param {number}  [options.mrpPrice]    – MRP / list price from batch
 * @returns {Object} productDefined
 */
export function buildProductDefined(obj, batchItem, options = {}) {
  const upc = options.upc ?? batchItem?.upc ?? obj?.upc ?? "";
  const batchno = options.batchno ?? batchItem?.batchno ?? null;
  const mBatchId = options.mBatchId ?? batchItem?.mBatchId ?? null;
  const price = options.price ?? batchItem?.price ?? obj?.sunitprice ?? 0;

  if (obj?.value === "03.9300" || obj?.name === "03.9300") {
    console.log("[BUG-1511] buildProductDefined for 03.9300: obj.interTaxId:", obj?.interTaxId, "| obj.interTaxRate:", obj?.interTaxRate, "| obj.interTaxName:", obj?.interTaxName, "| obj.cTaxId:", obj?.cTaxId, "| obj.mProductId:", obj?.mProductId);
  }

  return {
    batchno: batchno || null,
    description: obj?.description || "",
    discount: 0,
    discountName: "",
    imageurl: obj?.imageurl || "",
    isDecimal: obj?.isDecimal || false,
    isManualQty: obj?.isManualQty || false,
    restrictPosQtyEdit: obj?.restrictPosQtyEdit || "N",
    showAlternateUomInPos: obj?.showAlternateUomInPos || "N",
    interTaxId: obj?.interTaxId || "",
    interTaxRate: obj?.interTaxRate || 0,
    interTaxName: obj?.interTaxName || "",
    alternateUom: obj?.alternateUom || null,
    isPromoApplicable: obj?.isPromoApplicable || false,
    isReturn: false,
    mBatchId: mBatchId || null,
    mPricingruleId: null,
    name: obj?.name || "",
    name2: obj?.name2 || "",
    nettotal: 0,
    primaryOrderLine: null,
    productId: obj?.mProductId || null,
    realPrice: price || 0,
    listPrice: obj?.slistprice || 0,
    sunitprice: obj?.sunitprice || 0,
    returnQty: null,
    salePrice: price || 0,
    originalPrice: options.originalPrice ?? obj?.sunitprice ?? 0,
    productSegment: obj?.productSegment || "",
    mrpPrice: options.mrpPrice ?? batchItem?.listPrice ?? 0,
    stock: obj?.onhandQty || 0,
    tax: obj?.cTaxId || null,
    taxCategory: obj?.taxCategory || "",
    taxName: obj?.taxName || "",
    interTaxId: obj?.interTaxId || "",
    interTaxRate: obj?.interTaxRate || 0,
    interTaxName: obj?.interTaxName || "",
    taxFlag: obj?.taxFlag || "N",
    taxAmount: 0,
    taxRate: obj?.taxRate || 0,
    uom: obj?.csUomId || null,
    uom_name: obj?.uomName || "",
    uomData: obj?.uomData || [],
    addNewLine: obj?.addNewLine || "N",
    customAttributes: obj?.customAttributes || [],
    preparationTime: obj?.preparationTime || "",
    printerId: obj?.printerId || "",
    productionCenterId: obj?.productionCenterId || "",
    cwrPcatalogueId: obj?.cwrPcatalogueId || null,
    cwrMenuTimeslot: obj?.cwrMenuTimeslot || [],
    bunitName: obj?.bunitName || "",
    imageUrl: obj?.imageUrl || "",
    showAddons: obj?.showAddons || false,
    foodType: obj?.foodType || "",
    itemType: obj?.itemType || "",
    type: obj?.type || "",
    newCustomAttributes: [],
    priceList: obj?.priceList || [],
    selectedAddons: [],
    isDecimalQty: obj?.uomData?.[0]?.decimal === "Y" || false,
    isQtyDesimal: obj?.uomData?.[0]?.stdprecision ?? 2,
    upc: upc || "",
    value: obj?.value || "",
    weight: 0,
    order: "N",
    productionCenter: obj?.productionCenter || "",
    shortDescription: obj?.shortDescription || "",
    hsncode: obj?.hsncode || null,
    csBunitId: obj?.csBunitId || null,
    mProductCategoryId: obj?.mProductCategoryId || null,
    productManufacturerId: obj?.productManufacturerId || null,
    productBrandId: obj?.productBrandId || null,
    productBrandName: obj?.productBrandName || null,
    productCategoryName: obj?.productCategoryName || "",
    productAddons: obj?.productAddons || [],
    batchedProduct: obj?.batchedProduct || false,
    batchedForSale: obj?.batchedForSale || false,
    batchedForStock: obj?.batchedForStock || false,
    multiPrice: obj?.multiPrice || false,
    shelfLife: obj?.shelfLife || "",
    stdTax: (obj?.sunitprice || 0) * (1 + (obj?.taxRate || 0) / 100),
    posPriceEdit: obj?.posPriceEdit || "N",
    upcPricingRule: obj?.upcPricingRule || null,
  };
}

/**
 * useProductOperations
 *
 * Encapsulates all product-related functions formerly inline in PointOfsaleCore:
 * barcode input, product picking, weight handling, category/search, and stock verification.
 */
const useProductOperations = ({
  cart,
  setCart,
  tillData,
  tillLayout,
  posConfig,
  precision,
  isExchange,
  authHeaders,
  addProduct,
  setSelectedProductInCart,
  selectedProductInCart,
  setQtyNumberFlag,
  setInputFocused,
  setDisplayBatchSelection,
  setBatchSetAvailable,
  setDisplayManualQtyWeightInput,
  setProductStock,
  setTempCart,
  setShowCompleteSaleStock,
  setCompleteSaleStockData,
  setShowPaymentMethods,
  setOptions,
  setActiveInput,
  setNumb,
  paymentModalInputRef,
  productSearchInputRef,
  keyboard,
  searchInputRef,
  setFilterDrawer,
  setLayoutType,
  productsCopy,
  fetchOnlineBatches,
  handleCartTotalClick,
  setIsStockModal,
  // Customer / PAN related
  isNriBilling,
  retailCustomerRules,
  isPanCustomer,
  setPanDrawerMode,
  setSelectedEditOldCustomer,
  convertMetaDataToKeyValue,
  retailCustomerFields,
  customerForm,
  toCamelCase,
  setDocuments,
  setIsPanUpdateMode,
  setDisplayAddNewCustomer,
  hasShownCustomerModalRef,
  setCustomerSearchTrigger,
  setDisplayCustomerSearch,
  setDisplayEditOldCustomer,
  // Sales rep
  setOpenSalesRep,
  // Dispatch
  dispatchWeight,
  // Product search input state
  productSearchInput,
  setProductSearchInput,
  history,
  // isRefundWalletOpen not needed; verifyStock uses setIsRefundWalletOpen? No, it doesn't.
}) => {
  const { t } = useTranslation();

  const { safeSetTimeout } = useSafeTimeout();

  // ── State ──────────────────────────────────────────────────────────────

  // Product weight modal
  const [productWeightModalInput, setProductWeightModalInput] = useState("");
  const [currentWeightSelectedProduct, setCurrentWeightSelectedProduct] = useState({});

  // Product filter
  const [isProductsFilter, setIsProductsFilter] = useState(() => (posConfig?.defaultSearchScreen === "Category Search" ? true : false));
  const [allProductCategories, setAllProductCategories] = useState([]);
  const [selectedProductBrand, setSelectedProductBrand] = useState([]);
  const [selectedProductCategory, setSelectedProductCategory] = useState("allProducts");
  const [selectCategotyList, setSelectCategotyList] = useState([]);
  const [productsData, setProductsData] = useState([]);
  const prevProductsListRef = useRef(null);

  // "Place of Supply" confirmation dedupe — we only want the mismatch dialog
  // once per checkout attempt. Reset when the cart is cleared / customer
  // changes. Ported from roadmap bc3b9831 (supplyRegionProceed).
  const supplyRegionProceedRef = useRef(false);

  // Reset "Place of Supply" confirmation when the customer changes (new
  // customer may have a different region) or when the cart is cleared after
  // order completion (customer resets to default, which triggers this).
  useEffect(() => {
    supplyRegionProceedRef.current = false;
  }, [cart?.customer?.cwrCustomerId]);

  // Product search
  const [isSearchProducts, setIsSearchProducts] = useState(() =>
    posConfig?.defaultSearchScreen === "Product Search" || posConfig?.defaultSearchScreen === "Category Search" || posConfig?.defaultSearchScreen === undefined ? true : false
  );
  const [isProductsVisible, setIsProductsVisible] = useState(false);

  const productListCardRef = useRef(null);
  const barcodeInFlightRef = useRef(false);
  const addDefinedProductInFlightRef = useRef(false);

  // Manual batch entry for receiptless returns
  const [showManualBatchPopup, setShowManualBatchPopup] = useState(false);
  const [manualBatchNo, setManualBatchNo] = useState("");
  const [manualBatchProduct, setManualBatchProduct] = useState(null);

  // ── Barcode & Product Add ─────────────────────────────────────────────

  const onBarcodeInput = async (data, flag) => {
    if (barcodeInFlightRef.current) return;
    barcodeInFlightRef.current = true;
    try {
      keyboard?.current?.setInput("");
      let chackBarcodeFlag = false;
      const trimmedData = typeof data === "string" ? data.trim() : data;
      if (trimmedData && trimmedData !== "") {
        await barcodeScaner(
          trimmedData,
          tillData,
          tillLayout,
          addDefinedProduct,
          setBatchSetAvailable,
          setDisplayBatchSelection,
          setLayoutType,
          setIsProductsVisible,
          setProductsData,
          chackBarcodeFlag,
          setSelectedProductInCart,
          setProductSearchInput,
          productSearchInputRef,
          cart,
          authHeaders,
          setOptions,
          fetchOnlineBatches,
          setCart,
          setProductStock,
          setTempCart,
          setShowCompleteSaleStock,
          setCompleteSaleStockData,
          setShowPaymentMethods,
          history
        );
      }
    } finally {
      barcodeInFlightRef.current = false;
    }
  };

  const verifyStock = async (docType) => {
    const selectedReturnType = useTransactionStore.getState().selectedReturnType;
    const exchangeCartData = useCartStore.getState().exchangeCart;
    // Read FRESH cart from store — verifyStock is sometimes re-invoked
    // immediately after setCart (e.g. after customer search selection in
    // proceedWithCustomerSelection). The closure-captured `cart` would be
    // stale, causing the PAN drawer to open with the OLD default customer
    // instead of the just-selected one. (Issue #7)
    const freshCart = useCartStore.getState().cart || cart;
    const activeCart = selectedReturnType === "EXCHANGE" && exchangeCartData ? exchangeCartData : freshCart;

    // Fail fast on customerRequired before opening the payment drawer. The
    // backstop in processOrder still catches the same case at submit time, but
    // by then the cashier has already entered payment details — surfacing the
    // requirement up front matches operator expectations. Returns are exempted
    // because refund flows often run against the default B2C anonymous record.
    // (Tab8 issue #1)
    if (posConfig?.customerRequired === "Y" && activeCart?.isReturn !== true) {
      const b2cId = tillData?.tillAccess?.csBunit?.b2cCustomer?.cwrCustomerId;
      const cartCustomerId = activeCart?.customer?.cwrCustomerId;
      if (!cartCustomerId || cartCustomerId === b2cId) {
        message.warning("Customer is required to complete this order. Please add a customer.");
        return;
      }
    }
    const defaultCustomer = activeCart?.customer?.cwrCustomerId === tillData?.tillAccess?.csBunit?.b2cCustomer?.cwrCustomerId;
    const isAnonymousCustomer = defaultCustomer && posConfig?.showCustomerSearchValue === "Before Payment";

    const checkConditionalRules = async () => {
      if (isNriBilling || activeCart?.customer?.nriBilling === true) {
        return false;
      }
      const panRule = retailCustomerRules?.find((rule) => rule.action === "Request PAN");

      if (!panRule || Number(activeCart.total) <= Number(panRule.value) || activeCart?.customer?.b2cRegisteredstoreId === null) {
        return false;
      }

      const finalPAN = activeCart?.customer?.metaData?.find((m) => m.key?.toLowerCase().startsWith("pan card"))?.value || activeCart?.customer?.panCard;
      if (!finalPAN || finalPAN.trim() === "") {
        setPanDrawerMode("THRESHOLD");
        return true;
      }

      // Customer already has PAN and valid customerProperties from a prior
      // update in this transaction — skip re-opening the drawer. (BUG-0777)
      if (activeCart?.customer?.customerProperties?.isValid) {
        return false;
      }
      setSelectedEditOldCustomer(activeCart?.customer);
      setIsPanUpdateMode(true);
      setPanDrawerMode("VALIDATION");
      return true;
    };

    const performStockCheck = async () => {
      if (posConfig?.enableStockCheck === "N" || !posConfig?.enableStockCheck) {
        // DI-S1 advisory stock check moved to processOrderApproved (final submit)
        // so it doesn't fire prematurely on "Total Amount To Pay" click.
        handleCartTotalClick(docType);
        return;
      }

      const isFinalValidation = true;
      const stockCheckResult = await stock({
        cart,
        setCart,
        tillData,
        setProductStock,
        isFinalValidation,
        setTempCart,
      });

      setSelectedProductInCart({});

      if (stockCheckResult) {
        // Forward the original docType — without this, requireDeliveryDate-
        // Advance gating in handleCartTotalClick can't detect Advance flow and
        // skips the "Please select a Delivery Date" warning. (Issue #15)
        handleCartTotalClick(docType);
      } else {
        setIsStockModal(true);
      }
    };

    // "Place of Supply" mismatch — fires when the customer's registered state
    // differs from the store's billing state. Reads customer state from the
    // B2B "Invoicing Address" or retail "Address" fieldGroup (configured in
    // customerConfigs). Only fires for retail (non-B2B) customers, because
    // B2B invoicing address is already the billing state. If the backend
    // hasn't configured fieldGroups, this silently no-ops. Ported from
    // roadmap bc3b9831 with defensive fallbacks.
    const checkSupplyRegionMismatch = async () => {
      if (supplyRegionProceedRef.current) return true;
      if (!retailCustomerFields || retailCustomerFields.length === 0) return true;

      const storeStateId = tillData?.tillAccess?.csBunit?.csRegionId || tillData?.tillAccess?.csBunit?.customerAddress?.csRegion?.csRegionID || "";
      if (!storeStateId) return true;

      let customerConfigs;
      try {
        // customerConfigs = JSON.parse(localStorage.getItem("customerConfigs") || "[]");
        const referenceStoreData = JSON.parse(localStorage.getItem("referenceStore") || "{}");
        customerConfigs = referenceStoreData?.state?.customerConfigs || [];
      } catch {
        customerConfigs = [];
      }
      let customerConfigData = {};
      try {
        customerConfigData = customerConfigs?.[0]?.configuration ? JSON.parse(customerConfigs[0].configuration) : {};
      } catch {
        customerConfigData = {};
      }
      const fieldGroups = customerConfigData?.fieldGroups || [];
      if (fieldGroups.length === 0) return true;

      const storedB2B = localStorage.getItem("isB2bEnabled") === "true";
      const groupName = storedB2B ? "Delivery Address" : "Address";
      const groupId = fieldGroups.find((g) => g.name === groupName)?.id;
      if (!groupId) return true;

      let customerStateId = "";
      try {
        const metaRow = activeCart?.customer?.metaData?.find((item) => item.key === groupId);
        if (metaRow?.value) {
          const parsed = typeof metaRow.value === "string" ? JSON.parse(metaRow.value) : metaRow.value;
          customerStateId = parsed?.state || "";
        }
      } catch {
        customerStateId = "";
      }

      // const statesData = JSON.parse(localStorage.getItem("statesData") || "[]");
      let statesData = [];

      try {
        const referenceStoreData = JSON.parse(localStorage.getItem("referenceStore") || "{}");
        statesData = referenceStoreData?.state?.statesData || [];
      } catch {
        statesData = [];
      }
      if (customerStateId) {
        const isId = statesData.some((s) => s.RecordID === customerStateId);
        if (!isId) {
          const match = statesData.find((s) => s.Name?.toLowerCase() === customerStateId?.toLowerCase());
          customerStateId = match?.RecordID || "";
        }
      }

      const isDifferent = customerStateId && storeStateId !== customerStateId;
      if (storedB2B || !isDifferent) return true;

      return new Promise((resolve) => {
        Modal.confirm({
          title: "Place of Supply Mismatch",
          content: "Customer region and store region are different. Do you want to proceed or change place of supply?",
          okText: "Proceed",
          cancelText: "Change",
          okButtonProps: { style: { display: "inline-block" } },
          onOk: () => {
            supplyRegionProceedRef.current = true;
            resolve(true);
          },
          onCancel: () => {
            const cartDetails = useCartStore.getState().cart;
            setSelectedEditOldCustomer(cartDetails?.customer);
            const mappedCustomer = convertMetaDataToKeyValue(activeCart?.customer);
            const formValues = {};
            const normalize = (str) => str?.toLowerCase().replace(/\s+/g, "");
            const dynamicMapping = {};
            Object.keys(mappedCustomer || {}).forEach((key) => {
              dynamicMapping[normalize(key)] = key;
            });
            retailCustomerFields.forEach((field) => {
              const fieldKey = normalize(field.name);
              const mappedKey = dynamicMapping[fieldKey];
              let value;
              if (field.groupId) {
                const groupData = mappedCustomer[field.groupId] || {};
                value = groupData[toCamelCase(field.name)] ?? groupData[normalize(field.name)];
              } else {
                value = mappedCustomer[mappedKey];
              }
              if (field.groupId) {
                if (!formValues[field.groupId]) formValues[field.groupId] = {};
                formValues[String(field.groupId).toLowerCase()][toCamelCase(field.name)] = value;
              } else {
                formValues[toCamelCase(field.name)] = value;
              }
            });
            customerForm.setFieldsValue(formValues);
            if (setDisplayEditOldCustomer) setDisplayEditOldCustomer(true);
            resolve(false);
          },
        });
      });
    };

    const selectedReturnTypeNow = useTransactionStore.getState().selectedReturnType;
    const exchangeCartObj = useCartStore.getState().exchangeCart;

    if (selectedReturnTypeNow === "EXCHANGE") {
      if (!exchangeCartObj?.items?.length) {
        message.warning("Please add items to the exchange cart before proceeding to payment.");
        return;
      }
      const exchangeTotal = Number(exchangeCartObj?.total) || 0;
      const returnTotal = Math.abs(Number(cart?.total) || 0);
      if (exchangeTotal < returnTotal) {
        message.warning("Exchange value must be equal to or greater than Return value.");
        return;
      }
    }

    // AllowPrice Edit at POS validation — block checkout when any item
    // requires manual price entry but still has zero/missing price.
    const zeroPriceItems = activeCart?.items?.filter((item) => item.posPriceEdit === "Y" && (!item.realPrice || Number(item.realPrice) <= 0));
    if (zeroPriceItems?.length > 0) {
      Modal.warning({
        title: "Price Required",
        content: (
          <div>
            <p style={{ marginBottom: 8 }}>Price has not been entered for the following products. Please enter a valid price to continue.</p>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr style={{ background: "#fafafa" }}>
                  <th style={{ textAlign: "left", padding: "6px 8px", borderBottom: "1px solid #f0f0f0" }}>Product Name</th>
                  <th style={{ textAlign: "left", padding: "6px 8px", borderBottom: "1px solid #f0f0f0" }}>Product Code</th>
                </tr>
              </thead>
              <tbody>
                {zeroPriceItems.map((item, idx) => (
                  <tr key={idx} style={{ borderBottom: "1px solid #f0f0f0" }}>
                    <td style={{ padding: "6px 8px" }}>{item.name || "—"}</td>
                    <td style={{ padding: "6px 8px" }}>{item.value || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ),
        okText: "OK",
        cancelButtonProps: { style: { display: "none" } },
      });
      return;
    }

    const isPanRuleViolated = await checkConditionalRules();
    // Customer search takes priority over sales rep — opening both at once
    // (issue #14) made the user pick a customer while a sales rep drawer
    // is also visible, leading to a confused state. The sales rep drawer
    // is re-triggered downstream in CustomerDrawerFooter after the customer
    // is set, so we only open it here if no customer prompt is needed.
    const willOpenCustomerSearch = isAnonymousCustomer && !hasShownCustomerModalRef.current && activeCart.total > 0;

    if (posConfig?.requireSalesRepresentative === "Y" && !isPanRuleViolated && !willOpenCustomerSearch) {
      const exchangeCartObjForRep = useCartStore.getState().exchangeCart;
      if (exchangeCartObjForRep && exchangeCartObjForRep?.items?.length > 0) {
        if (!exchangeCartObjForRep?.salesRepId) {
          setOpenSalesRep({ flag: true, level: "order" });
        }
      } else {
        if (!activeCart?.salesRepId) {
          setOpenSalesRep({ flag: true, level: "order" });
        }
      }
    }

    if (isPanRuleViolated) {
      if (defaultCustomer) {
        hasShownCustomerModalRef.current = true;
        setCustomerSearchTrigger("verifyStock");
        setDisplayCustomerSearch(true);
      } else {
        const mappedCustomer = convertMetaDataToKeyValue(activeCart?.customer);
        const formValues = {};
        const normalize = (str) => str?.toLowerCase().replace(/\s+/g, "");

        const dynamicMapping = {};
        Object.keys(mappedCustomer || {}).forEach((key) => {
          dynamicMapping[normalize(key)] = key;
        });
        retailCustomerFields.forEach((field) => {
          const fieldKey = normalize(field.name);
          const mappedKey = dynamicMapping[fieldKey];

          let value;
          if (field.groupId) {
            const groupKey = field.groupId;
            const groupData = mappedCustomer[groupKey] || {};
            value = groupData[toCamelCase(field.name)] ?? groupData[normalize(field.name)];
          } else {
            value = mappedCustomer[mappedKey];
          }

          if (field.groupId) {
            if (!formValues[field.groupId]) {
              formValues[field.groupId] = {};
            }
            formValues[String(field.groupId).toLowerCase()][toCamelCase(field.name)] = value;
          } else {
            formValues[toCamelCase(field.name)] = value;
          }
        });
        customerForm.setFieldsValue(formValues);
        const meta = activeCart?.customer?.metaData || [];
        const panMeta = meta.find((m) => m.key === "PAN_CARD_URL");
        const docMeta = meta.find((m) => m.key === "CUSTOMER_DOCUMENT_URLS");

        const docsToSet = [];

        if (panMeta?.value) {
          docsToSet.push({ docID: "", docName: "PAN Card", docUrl: panMeta.value, fileName: panMeta.value.split("/").pop() });
        }

        if (docMeta?.value) {
          try {
            JSON.parse(docMeta.value).forEach((url, i) => {
              docsToSet.push({ docID: `existing_${i}`, docName: "Customer Document", docUrl: url, fileName: url.split("/").pop() });
            });
          } catch (err) {
            console.error("[ProductOperations] Failed to parse customer document metadata:", err);
          }
        }

        setDocuments(docsToSet);

        setIsPanUpdateMode(true);
        setDisplayAddNewCustomer(true);
      }
    } else if (willOpenCustomerSearch) {
      hasShownCustomerModalRef.current = true;
      setCustomerSearchTrigger("verifyStock");
      setDisplayCustomerSearch(true);
    } else {
      const okToProceed = await checkSupplyRegionMismatch();
      if (okToProceed) {
        await performStockCheck();
      }
    }
  };

  const addDefinedProduct = async (productObjs, upc, batchno, mBatchId, price, checkWeight, modifiedQty, batchedItem, modifiedPrice, scanFlag, data) => {
    if (addDefinedProductInFlightRef.current) return;
    addDefinedProductInFlightRef.current = true;
    try {
      const cartObj = isExchange ? useCartStore.getState().exchangeCart || {} : useCartStore.getState().cart;
      if (productObjs?.productSegment === "AD") {
        const isAlreadyInCart = cartObj?.items?.some((item) => item?.value === productObjs?.value);
        if (isAlreadyInCart) {
          message.warning("This advance item is already present in the cart and cannot be added again.");
          return;
        }
      }

      setFilterDrawer(false);
      setIsProductsVisible(false);
      setQtyNumberFlag(0);
      const productObj = { ...productObjs };
      if (productObj.overRideTax === "Y" && price <= productObj.overRideCondition) {
        // prettier-ignore
        const originalPrice = price - (price - (price * (100 / (100 + productObj.taxRate))));
        const taxedPrice = originalPrice + (originalPrice * productObj.contraRate) / 100;
        price = taxedPrice;
        productObj.cTaxId = productObj.contraTaxId;
        productObj.taxRate = productObj.contraRate;
      }

      if (!scanFlag && productObj?.priceList?.length > 0) {
        const selectedPrice = getApplicablePrice({
          priceList: productObj.priceList,
          selectedUomId: productObj.uomData[0]?.csUomId,
          sPriceListId: cartObj?.customer?.sPriceListId,
          alternateUom: productObj?.alternateUom || [],
          primaryUomId: productObj?.uom || null,
        });

        if (selectedPrice && !(shouldUseBatchPrice(productObj) && mBatchId)) {
          productObj.originalPrice = productObj.sunitprice;
          productObj.sunitprice = selectedPrice.pricestd;
          price = selectedPrice.pricestd;
        }
      }

      let productDefined = buildProductDefined(productObj, null, {
        upc,
        batchno,
        mBatchId,
        price,
        originalPrice: productObj?.originalPrice || productObj?.sunitprice,
      });

      let updatedWeight;
      const hasAlternateUom = productDefined.alternateUom?.length > 0 && Array.isArray(productDefined.alternateUom) && productDefined.showAlternateUomInPos === "Y";
      // When isManualQty + customAttributes are both present, skip the weight popup —
      // the CustomAttributesDrawer will collect the weight directly.
      const hasCustomAttrs = (productDefined.customAttributes?.length || 0) > 0;
      if (
        (hasAlternateUom || (productDefined.isManualQty && posConfig?.showWeightPopup === "Y")) &&
        !(productDefined.isManualQty && hasCustomAttrs) &&
        (modifiedQty === 1 || modifiedQty === 0 || !modifiedQty) &&
        data?.length !== 13
      ) {
        setOptions([]);
        setActiveInput("weightPopup");
        const updateProductWithWeight = new Promise((resolve) => {
          dispatchWeight(productDefined, setInputFocused, selectedProductInCart, setSelectedProductInCart, (updatedProduct) => {
            if (updatedProduct) {
              updatedWeight = updatedProduct.weight;
              modifiedQty = updatedProduct.weight;
              productDefined = updatedProduct;
              resolve(updatedProduct);
            }
          });
        });

        const updatedProductDefined = await updateProductWithWeight;
        setCurrentWeightSelectedProduct(updatedProductDefined);
      }

      // Assign existing lineId AFTER UOM selection — match by productId AND uom to avoid merging different UOMs
      let cartItems = cartObj?.items;
      let filterItem = cartItems?.filter((item) => item.productId === productDefined.productId && item.uom === productDefined.uom);
      if (filterItem?.length === 1 && filterItem?.selectedAddons?.length <= 0) {
        productDefined.lineId = filterItem[0].lineId;
      }
      setActiveInput("search");
      if (searchInputRef?.current) {
        searchInputRef.current.focus();
      }
      if (checkWeight === true || updatedWeight) {
        let index = cartObj.items.findIndex((item) => item.value === productDefined.value);
        if (batchedItem === true && !updatedWeight) {
          modifiedQty = cartObj.items > 0 ? cart.items[index].weight + 1 : 1;
        }
        const productSearchInput = document.getElementById("sm-product-search");
        if (productSearchInput) {
          productSearchInput.blur();
        }
        await addProduct(productDefined, modifiedQty, false, modifiedPrice, false);
        setProductSearchInput("");
        localStorage.removeItem("productName");
        setOptions([]);
        if (productSearchInputRef?.current) {
          productSearchInputRef?.current?.clearInput();
        }
      } else {
        await addProduct(productDefined, 1, false, 0, false);
        setProductSearchInput("");
        localStorage.removeItem("productName");
        setOptions([]);
        if (productSearchInputRef?.current) {
          productSearchInputRef?.current?.clearInput();
        }
      }
    } finally {
      addDefinedProductInFlightRef.current = false;
    }
  };

  const selectProductToCart = (batch, qty = 1) => {
    batch.productAddons = batch.productAddons || [];
    batch.customAttributes = batch.customAttributes || [];
    batch.selectedAddons = batch.selectedAddons || [];

    addProduct(batch, qty, false, 0, false);
    setDisplayBatchSelection(false);
  };

  // ── Product Weight Modal ──────────────────────────────────────────────

  const onProductModalChangeWeight = (event) => {
    setProductWeightModalInput(event.target.value);
  };

  const handleWeightManual = (value) => {
    if (productWeightModalInput === "" && value === "x") {
      setProductWeightModalInput("");
    } else if (value === "x") {
      setProductWeightModalInput(`${productWeightModalInput.toString().substring(0, productWeightModalInput.toString().length - 1)}`);
    } else {
      setProductWeightModalInput(`${productWeightModalInput}${value}`);
    }
  };

  const pickProduct = (obj) => {
    setIsProductsVisible(false);
    const returnThisReceipt = useTransactionStore.getState().returnThisReceipt;
    const isExchange = useTransactionStore.getState().selectedReturnType === "EXCHANGE" && !returnThisReceipt;
    const activeCartForBatch = isExchange ? useCartStore.getState().exchangeCart || {} : useCartStore.getState().cart || cart;
    const isReceiptlessReturn = activeCartForBatch?.isReturn === true && activeCartForBatch?.isBlindReceipt === true;
    if (isReceiptlessReturn && obj.batchedProduct === "Y" && obj.batchedForSale === "Y") {
      setManualBatchProduct(obj);
      setManualBatchNo("");
      setShowManualBatchPopup(true);
      return;
    }
    if (obj.batchedProduct === "Y" && obj.batchedForSale === "Y" && Array.isArray(obj.mBatch) && obj.mBatch.length > 0) {
      if (obj.mBatch.length === 1) {
        setFilterDrawer(false);
        addDefinedProduct(obj, obj.mBatch[0].upc, obj.mBatch[0].batchno, obj.mBatch[0].mBatchId, obj.mBatch[0].price);
      } else {
        const productSet = [];
        const localObj = obj;
        for (let i = 0; i < obj.mBatch.length; i += 1) {
          const batchItem = { ...localObj.mBatch[i] };
          const batchObj = { ...localObj };
          if (batchObj.overRideTax === "Y" && batchItem.price <= batchObj.overRideCondition) {
            // prettier-ignore
            const originalPrice = batchItem.price - (batchItem.price - (batchItem.price * (100 / (100 + batchObj.taxRate))));
            const taxedPrice = originalPrice + (originalPrice * batchObj.contraRate) / 100;
            batchItem.price = taxedPrice;
            batchObj.cTaxId = batchObj.contraTaxId;
            batchObj.taxRate = batchObj.contraRate;
          }
          const productDefined = buildProductDefined(batchObj, batchItem, {
            mrpPrice: batchItem.listPrice || 0,
          });
          const selectedPrice = getApplicablePrice({
            priceList: batchObj?.priceList,
            selectedUomId: batchObj?.uomData?.[0]?.csUomId,
            sPriceListId: cart?.customer?.sPriceListId,
            alternateUom: batchObj?.alternateUom || [],
            primaryUomId: batchObj?.uom || null,
          });
          const useBatch = shouldUseBatchPrice(batchObj);
          productDefined.salePrice = useBatch ? batchItem.price : selectedPrice?.pricestd;
          productDefined.realPrice = useBatch ? batchItem.price : selectedPrice?.pricestd;
          productSet.push(productDefined);
        }
        setFilterDrawer(false);
        setBatchSetAvailable([...productSet]);
        fetchOnlineBatches(productSet);
      }
    } else {
      addDefinedProduct(obj, obj.upc, null, null, obj.sunitprice);
    }
  };

  const addManualBatchProduct = () => {
    if (!manualBatchNo?.trim() || !manualBatchProduct) return;
    const obj = manualBatchProduct;
    const productDefined = buildProductDefined(obj, null, {});
    productDefined.batchno = manualBatchNo.trim();
    productDefined.mBatchId = null;
    productDefined.realPrice = obj.sunitprice || 0;
    productDefined.salePrice = obj.sunitprice || 0;
    const selectedPrice = getApplicablePrice({
      priceList: obj?.priceList,
      selectedUomId: obj?.uomData?.[0]?.csUomId,
      sPriceListId: cart?.customer?.sPriceListId,
      alternateUom: obj?.alternateUom || [],
      primaryUomId: obj?.uom || null,
    });
    if (selectedPrice?.pricestd != null) {
      productDefined.salePrice = selectedPrice.pricestd;
      productDefined.realPrice = selectedPrice.pricestd;
    }
    addProduct(productDefined, 1, false, 0, false);
    setFilterDrawer(false);
    setShowManualBatchPopup(false);
    setManualBatchNo("");
    setManualBatchProduct(null);
    setIsProductsVisible(false);
  };

  const checkIsManualWeight = (prod) => {
    if (prod.isManualQty && posConfig?.showWeightPopup === "Y") {
      setDisplayManualQtyWeightInput(true);
      setCurrentWeightSelectedProduct(prod);
    } else {
      addProduct(prod, 1, false, 0, false);
    }
  };

  const modulo = (a, b) => {
    return a - b * Math.floor(a / b);
  };

  const addManualWeightToProduct = () => {
    let inputValidator = new RegExp(/^[0-9]*$/);
    if (currentWeightSelectedProduct.isDecimal) inputValidator = new RegExp(/^[0-9]\d*(\.\d+)?$/);
    if (inputValidator.test(productWeightModalInput) && parseFloat(productWeightModalInput) > 0) {
      setDisplayManualQtyWeightInput(false);
      addProduct(currentWeightSelectedProduct, productWeightModalInput, false, 0, false);
      safeSetTimeout(() => {
        setProductWeightModalInput("");
        setCurrentWeightSelectedProduct({});
      }, 500);
    } else {
      console.warn("Invalid weight input: ", productWeightModalInput);
      message.warning(`${messages.warnings.cart.invalid_input_value}`);
    }
  };

  // ── Product Filter & Search ───────────────────────────────────────────

  const callGetWeightAPI = async (productDefined) => {
    const productToUpdate = productDefined && Object.keys(productDefined)?.length > 0 ? productDefined : selectedProductInCart;
    const isKg = productToUpdate?.uomData?.[0]?.decimal === "Y" || productToUpdate?.isDecimalQty === true || productToUpdate?.isDecimal === true;
    if (!isKg) return;
    try {
      const response = await Axios({
        url: `${tillData?.tillAccess?.cwrTill?.hardwareController?.imageUrl}getWeight`,
        method: "POST",
      });
      if (response?.data?.Weight) {
        const weight = parseFloat(response.data.Weight.replace("kg", "").trim());
        if (!isNaN(weight)) {
          productToUpdate.weight = weight;
          setSelectedProductInCart(productToUpdate);
        }
      }
    } catch (error) {
      console.error("Error in callGetWeightAPI:", error);
    }
  };

  useEffect(() => {
    db.AllProductCategories.toArray().then((res) => {
      setAllProductCategories(res);
    });
  }, []);

  useEffect(() => {
    getCategoryProducts(selectedProductCategory);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const selectProductCategory = (category) => {
    if (selectedProductCategory !== category) {
      setSelectedProductCategory(category);
    }
  };

  const filterProducts = (brands, categories) => {
    let filteredItems = productsCopy;

    if (brands?.length > 0 || categories?.length > 0) {
      filteredItems = productsCopy.filter(
        (item) => (brands?.length === 0 || brands?.includes(item.brandId)) && (categories?.length === 0 || categories?.includes(item.mProductCategoryId))
      );
    }

    // Update the productsList with filtered items
  };

  // Debounced handler for brand checkbox changes — useRef keeps the same
  // debounced function across renders so the 300ms timer isn't reset on re-render.
  const debouncedBrandChangeRef = useRef(
    debounce((brandId, currentBrands, currentCategories, setFn, filterFn) => {
      const isSelected = currentBrands?.includes(brandId);
      const updatedSelectedBrands = isSelected ? currentBrands?.filter((id) => id !== brandId) : [...(currentBrands || []), brandId];
      setFn(updatedSelectedBrands || []);
      filterFn(updatedSelectedBrands || [], currentCategories);
    }, 300)
  );
  useEffect(() => () => debouncedBrandChangeRef.current.cancel(), []);

  const handleBrandCheckboxChange = (brandId) => {
    debouncedBrandChangeRef.current(brandId, selectedProductBrand, selectCategotyList, setSelectedProductBrand, filterProducts);
  };

  // Debounced handler for category checkbox changes
  const debouncedCategoryChangeRef = useRef(
    debounce((checkedValues, currentBrands, setFn, filterFn) => {
      setFn(checkedValues);
      filterFn(currentBrands, checkedValues);
    }, 300)
  );
  useEffect(() => () => debouncedCategoryChangeRef.current.cancel(), []);

  const handleCategoryCheckboxChange = (checkedValues) => {
    debouncedCategoryChangeRef.current(checkedValues, selectedProductBrand, setSelectCategotyList, filterProducts);
  };

  const handleSelectProduct = () => {
    prevProductsListRef.current = [];
    const lowerCaseSearchInput = productSearchInput.toLowerCase();
    const filteredProducts = productsCopy.filter(
      (product) =>
        product?.name?.toLowerCase().includes(lowerCaseSearchInput) ||
        product?.batchIndex === lowerCaseSearchInput ||
        product?.upcIndex === lowerCaseSearchInput ||
        product?.value === lowerCaseSearchInput ||
        product?.upc === lowerCaseSearchInput
    );
    if (filteredProducts.length === 0) {
      {
        message.info(`${t("product_search_category_error")}`);
      }
    } else {
    }
  };

  useEffect(() => {
    if (selectedProductCategory !== "all") {
      getCategoryProducts();
    }
  }, [selectedProductCategory]);

  const getMoreProducts = () => {
    if (productSearchInput === "") {
      getCategoryProducts(selectedProductCategory);
    }
  };

  const getCategoryProducts = () => {
    // Category products logic (currently commented out in original)
  };

  const getSearchedProducts = () => {
    db.products
      .filter((product) => product.isactive == true)
      .filter((product) => product.productSegment !== "BG")
      .filter(
        (product) =>
          (typeof product.name === "string" && product.name.toLowerCase().includes(productSearchInput.toLowerCase())) ||
          (typeof product.value === "string" && product.value.toLowerCase() === productSearchInput.toLowerCase())
      )
      .limit(100)
      .toArray()
      .then(async (productsFetched) => {
        // Out-of-stock code typed by the user isn't in local DB → fetch on-demand
        // once, then re-read so the existing selection logic below can consume it.
        // Guarded by length === 0 + a real search value so in-stock searches are
        // unchanged (no extra API call).
        if (productsFetched.length === 0 && productSearchInput && productSearchInput.trim().length >= 2) {
          try {
            const tillData = useAuthStore.getState().tillData;
            if (tillData?.tillAccess?.csBunit?.csBunitId) {
              const synced = await syncSingleProductByValue(tillData, uuidv4(), tillData?.tillAccess?.cwrTill?.cwrTillID, productSearchInput.trim());
              if (synced) {
                productsFetched = await db.products
                  .filter((product) => product.isactive == true)
                  .filter((product) => product.productSegment !== "BG")
                  .filter(
                    (product) =>
                      (typeof product.name === "string" && product.name.toLowerCase().includes(productSearchInput.toLowerCase())) ||
                      (typeof product.value === "string" && product.value.toLowerCase() === productSearchInput.toLowerCase())
                  )
                  .limit(100)
                  .toArray();
              }
            }
          } catch (err) {
            console.error("Core search on-demand sync failed:", err);
          }
        }
        if (productsFetched.length > 1) {
          setProductsData(productsFetched);
          setIsProductsVisible(true);
        } else if (productsFetched.length === 1) {
          const obj = productsFetched[0];

          if (obj.batchedProduct === "Y" && obj.batchedForSale === "Y" && Array.isArray(obj.mBatch) && obj.mBatch.length > 0) {
            if (Array.isArray(obj.mBatch) && obj.mBatch.length > 0) {
              const batchProductIndex = obj.mBatch.findIndex((bp) => bp.batchno.toLowerCase() === productSearchInput.toLowerCase());

              if (batchProductIndex >= 0 && obj.multiPrice === "N") {
                const batchItem = obj.mBatch[batchProductIndex];
                addDefinedProduct(obj, batchItem.upc || "", batchItem.batchno || "", batchItem.mBatchId || "", batchItem.price || 0);
                setNumb(0);
                paymentModalInputRef.current?.focus();
              } else if (obj.mBatch.length === 1) {
                const batchItem = obj.mBatch[0];
                addDefinedProduct(obj, batchItem.upc || "", batchItem.batchno || "", batchItem.mBatchId || "", batchItem.price || 0);
                paymentModalInputRef?.current?.focus();
                setNumb(0);
              } else {
                const productSet = [];
                const localObj = obj;

                for (let i = 0; i < obj.mBatch.length; i += 1) {
                  const batchItem = { ...localObj.mBatch[i] };
                  const newObj = { ...localObj };

                  if (newObj.overRideTax === "Y" && batchItem.price <= newObj.overRideCondition) {
                    const originalPrice = batchItem.price - (batchItem.price - batchItem.price * (100 / (100 + newObj.taxRate)));
                    const taxedPrice = originalPrice + (originalPrice * newObj.contraRate) / 100;
                    batchItem.price = taxedPrice;
                    newObj.cTaxId = newObj.contraTaxId;
                    newObj.taxRate = newObj.contraRate;
                  }

                  const productDefined = buildProductDefined(newObj, batchItem, {
                    mrpPrice: batchItem.listPrice || 0,
                  });
                  const useBatch = shouldUseBatchPrice(newObj);
                  if (!useBatch) {
                    const selectedPrice = getApplicablePrice({
                      priceList: newObj?.priceList,
                      selectedUomId: newObj?.uomData?.[0]?.csUomId,
                      sPriceListId: cart?.customer?.sPriceListId,
                      alternateUom: newObj?.alternateUom || [],
                      primaryUomId: newObj?.uom || null,
                    });
                    if (selectedPrice) {
                      productDefined.salePrice = selectedPrice.pricestd;
                      productDefined.realPrice = selectedPrice.pricestd;
                    }
                  }
                  productSet.push(productDefined);
                }
                setBatchSetAvailable([...productSet]);
                fetchOnlineBatches(productSet);
              }
            }
          } else {
            addDefinedProduct(obj, obj.upc || "", null, null, obj.sunitprice || 0);
            paymentModalInputRef?.current?.focus();
            setProductSearchInput("");
            localStorage.removeItem("productName");
            setOptions([]);
            if (productSearchInputRef?.current) {
              productSearchInputRef?.current?.clearInput();
            }
            setNumb(0);
          }
        } else {
          message.warning(`${t("product_search_error")}`);
        }
      });
  };

  const kioskFilteredProducts = (filter) => {
    db.products
      .filter((product) => product.isactive == true)
      .filter((product) => product.productSegment !== "BG")
      .limit(100)
      .toArray()
      .then((productsFetched) => {
        if (productsFetched.length > 1) {
          let filteredData = [];
          productsFetched.forEach((item) => {
            const itemName = item.name[0].toUpperCase();

            if (filter.includes("-")) {
              const filters = filter.split("-");
              const start = filters[0];
              const end = filters[filters.length - 1];

              if (itemName >= start && itemName <= end) {
                filteredData.push(item);
              }
            } else if (itemName === filter) {
              filteredData.push(item);
            }
          });

          setIsProductsVisible(true);

          if (filter === "All") {
            // setProductsList([...productsFetched]);
          } else {
            // setProductsList([...filteredData]);
          }
        }
      });
  };

  const getSearchedItem = () => {
    const term = (productSearchInput || "").toLowerCase();
    db.products
      .filter((p) => {
        if (p.isactive != true || p.productSegment === "BG") return false;
        return (
          (p.name || "").toLowerCase().startsWith(term) ||
          (p.batchIndex || "").toLowerCase() === term ||
          (p.upcIndex || "").toLowerCase() === term ||
          (p.value || "").toLowerCase() === term
        );
      })
      .limit(100)
      .toArray()
      .then((product) => {
        if (product.length > 0) {
          const obj = product[0] || {};
          const mBatch = obj.mBatch || [];
          const uomData = obj.uomData || [{}];
          const defaultPrice = obj.sunitprice || 0;

          if (obj.batchedProduct === "Y" && obj.batchedForSale === "Y" && Array.isArray(obj.mBatch) && obj.mBatch.length > 0) {
            const batchProductIndex = mBatch.findIndex((bp) => bp.batchno?.toLowerCase() === (productSearchInput || "").toLowerCase());
            if (batchProductIndex >= 0 && obj.multiPrice === "N") {
              addDefinedProduct(
                obj,
                mBatch[batchProductIndex]?.upc || "",
                mBatch[batchProductIndex]?.batchno || "",
                mBatch[batchProductIndex]?.mBatchId || "",
                mBatch[batchProductIndex]?.price || 0
              );
            } else if (mBatch.length === 1) {
              addDefinedProduct(obj, mBatch[0]?.upc || "", mBatch[0]?.batchno || "", mBatch[0]?.mBatchId || "", mBatch[0]?.price || 0);
            } else {
              const productSet = [];
              for (let i = 0; i < mBatch.length; i++) {
                const batchItem = { ...mBatch[i] };
                const productDefined = buildProductDefined(obj, batchItem, {
                  mrpPrice: batchItem.listPrice || 0,
                });
                const useBatch = shouldUseBatchPrice(obj);
                if (!useBatch) {
                  const selectedPrice = getApplicablePrice({
                    priceList: obj?.priceList,
                    selectedUomId: obj?.uomData?.[0]?.csUomId,
                    sPriceListId: cart?.customer?.sPriceListId,
                    alternateUom: obj?.alternateUom || [],
                    primaryUomId: obj?.uom || null,
                  });
                  if (selectedPrice) {
                    productDefined.salePrice = selectedPrice.pricestd;
                    productDefined.realPrice = selectedPrice.pricestd;
                  }
                }
                productSet.push(productDefined);
              }
              setBatchSetAvailable([...productSet]);
              fetchOnlineBatches(productSet);
            }
          } else {
            addDefinedProduct(obj, obj.upc || "", null, null, defaultPrice);
          }
        } else {
          message.warning(`${t("product_search_error")}`);
        }
      });
  };

  const clearProductSearchResults = () => {
    setProductSearchInput("");
    setOptions([]);
    if (keyboard.current) {
      keyboard.current.setInput("");
    }
    localStorage.removeItem("productName");
  };

  const closeProductPanel = () => {
    const productSearchInput = document.getElementById("sm-product-search");
    productSearchInput.focus();
    setIsSearchProducts(false);
    setIsProductsVisible(false);
    setIsProductsFilter(false);
    setProductSearchInput("");
    clearProductSearchResults();
    setProductsData([]);
    localStorage.removeItem("productName");
  };

  // ── Return ────────────────────────────────────────────────────────────

  return {
    // State
    productWeightModalInput,
    setProductWeightModalInput,
    currentWeightSelectedProduct,
    setCurrentWeightSelectedProduct,
    isProductsFilter,
    setIsProductsFilter,
    allProductCategories,
    setAllProductCategories,
    selectedProductBrand,
    setSelectedProductBrand,
    selectedProductCategory,
    selectCategotyList,
    setSelectCategotyList,
    productsData,
    setProductsData,
    prevProductsListRef,
    isSearchProducts,
    setIsSearchProducts,
    isProductsVisible,
    setIsProductsVisible,
    productListCardRef,

    // Manual batch entry for receiptless returns
    showManualBatchPopup,
    setShowManualBatchPopup,
    manualBatchNo,
    setManualBatchNo,
    manualBatchProduct,
    addManualBatchProduct,

    // Functions
    onBarcodeInput,
    verifyStock,
    addDefinedProduct,
    selectProductToCart,
    onProductModalChangeWeight,
    handleWeightManual,
    pickProduct,
    checkIsManualWeight,
    modulo,
    addManualWeightToProduct,
    callGetWeightAPI,
    selectProductCategory,
    filterProducts,
    handleBrandCheckboxChange,
    handleCategoryCheckboxChange,
    handleSelectProduct,
    getMoreProducts,
    getCategoryProducts,
    getSearchedProducts,
    kioskFilteredProducts,
    getSearchedItem,
    clearProductSearchResults,
    closeProductPanel,
  };
};

export default useProductOperations;
